import React from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Sparkles, Heart, Users, Star, Target } from 'lucide-react';
import { motion } from 'framer-motion';

const values = [
  {
    icon: <Heart className="w-6 h-6 text-pink-500" />,
    title: "Bienveillance",
    description: "Nous accompagnons chaque personne avec empathie et respect dans son parcours énergétique."
  },
  {
    icon: <Star className="w-6 h-6 text-yellow-500" />,
    title: "Excellence",
    description: "Nous nous engageons à fournir des outils précis et des conseils personnalisés de haute qualité."
  },
  {
    icon: <Target className="w-6 h-6 text-blue-500" />,
    title: "Innovation",
    description: "Nous développons constamment nos outils pour offrir la meilleure expérience possible."
  }
];

const testimonials = [
  {
    name: "Marie L.",
    role: "Utilisatrice",
    content: "Cette application m'a permis de mieux comprendre mon énergie et d'améliorer mon bien-être quotidien."
  },
  {
    name: "Thomas B.",
    role: "Coach en développement personnel",
    content: "Un outil précieux pour accompagner mes clients dans leur développement personnel."
  }
];

const stats = [
  { label: "Utilisateurs actifs", value: "1,000+" },
  { label: "Diagnostics réalisés", value: "5,000+" },
  { label: "Taux de satisfaction", value: "98%" }
];

export default function Mission() {
  return (
    <div className="min-h-screen pt-20 px-4 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100">
      <div className="max-w-4xl mx-auto space-y-6">
        <Card className="backdrop-blur-sm bg-white/90">
          <CardHeader>
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-8 h-8 text-[#54075B]" />
              <h1 className="text-3xl font-bold">Notre Mission</h1>
            </div>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Mission Statement */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="prose max-w-none"
            >
              <p className="text-lg text-gray-700 leading-relaxed">
                Le Labo HED du Cabinet Immersion 7 a conçu le Baromètre Énergétique avec pour mission est d'accompagner chaque individu dans la découverte de son potentiel énergétique et de sa véritable essence. Nous croyons que l'équilibre intérieur est la clé d'une vie épanouissante, et notre objectif est de fournir des outils simples, précis et accessibles pour aider chacun à comprendre, harmoniser et aligner son énergie.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mt-4">
                Notre approche repose sur le Human Design, une méthode holistique qui combine science, spiritualité et sagesse ancestrale pour révéler les énergies qui vous définissent. Nous souhaitons offrir un espace bienveillant et inspirant où chaque utilisateur peut :
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                <li>Découvrir ses forces et ses talents uniques</li>
                <li>Identifier les déséquilibres énergétiques pour mieux les surmonter</li>
                <li>Suivre des recommandations pratiques et adaptées à ses besoins</li>
                <li>Suivre son évolution au fil du temps grâce à des outils personnalisés</li>
              </ul>
            </motion.div>

            {/* Nos Valeurs */}
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold text-[#54075B]">Nos Valeurs</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {values.map((value, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.2 }}
                    className="p-6 bg-white rounded-lg shadow-sm"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      {value.icon}
                      <h3 className="font-semibold text-lg">{value.title}</h3>
                    </div>
                    <p className="text-gray-600">{value.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Statistiques */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.2 }}
                  className="text-center p-6 bg-white rounded-lg shadow-sm"
                >
                  <div className="text-3xl font-bold text-[#54075B]">{stat.value}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </motion.div>
              ))}
            </div>

            {/* Témoignages */}
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-[#54075B] text-center">Ce qu'ils en disent</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {testimonials.map((testimonial, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.3 }}
                    className="bg-white p-6 rounded-lg shadow-sm"
                  >
                    <p className="text-gray-600 italic mb-4">"{testimonial.content}"</p>
                    <div className="font-medium">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Engagement */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-[#54075B]/5 p-6 rounded-lg"
            >
              <h2 className="text-xl font-semibold text-[#54075B] mb-4">Notre Engagement</h2>
              <p className="text-gray-700">
                En mettant la puissance de la technologie au service de la connaissance de soi, nous visons à simplifier ce qui peut sembler complexe et à rendre chaque expérience enrichissante. Que vous soyez en quête d'équilibre, de clarté ou de transformation, nous sommes là pour vous guider pas à pas, dans la bienveillance et l'écoute.
              </p>
            </motion.div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}